import { type TokenClaims } from './types';

export const resetClaims = (): TokenClaims => ({
  board: new Set(),
});

/**
 * Create an abort signal that will be aborted after the given time.
 * @param timeMs The time in milliseconds to wait before aborting.
 */
export function abortSignalWithTimeout(timeMs: number) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeMs);
  return {
    signal: controller.signal,
    clear: () => clearTimeout(timeoutId),
  };
}

/**
 * Returns a promise that will resolve with the given value, or reject if the
 * given AbortSignal is aborted.
 * @param value The value to resolve with.
 * @param abortSignal The AbortSignal to listen to.
 */
export async function withAbortSignal<T>(
  value: Promise<T>,
  abortSignal: AbortSignal,
  abortErrorMessage: string,
): Promise<T> {
  if (abortSignal.aborted) {
    return Promise.reject(new Error(abortErrorMessage));
  }

  const abortPromise = new Promise<T>((_, reject) => {
    abortSignal.addEventListener(
      'abort',
      () => {
        reject(new Error(abortErrorMessage));
      },
      {
        once: true,
      },
    );
  });

  return Promise.race<T>([abortPromise, value]);
}
